// React
import React, { useEffect, useRef, useState, useMemo } from "react";

// D3
import * as d3 from "d3";

// Hooks
import useResponsiveState from "../../../../hooks/useResponsiveState";

// Styling
import "./graph.css";

// --- 1. DATA PROCESSING ---
const processData = (data, viewMode) => {
  if (!data || data.length === 0) return [];

  const cleanData = data
    .map((d) => ({ ...d, value: +d.value }))
    .filter((d) => d.category && !isNaN(d.value))
    .sort((a, b) => new Date(a.category) - new Date(b.category));

  if (viewMode === "Y") {
    const dataByMonth = d3.group(cleanData, (d) => {
      const date = new Date(d.category);
      return `${date.getFullYear()}-${date.getMonth()}`;
    });

    const aggregated = Array.from(dataByMonth, ([key, values]) => {
      const avgScore = d3.mean(values, (v) => v.value);
      const [year, month] = key.split("-");
      return {
        category: new Date(parseInt(year), parseInt(month), 1),
        value: avgScore,
        ids: values.map((v) => v.id),
      };
    });

    return aggregated.sort((a, b) => a.category - b.category);
  }

  return cleanData;
};

// --- HELPER: Single-letter month for compact mobile labels ---
const monthLetterFormat = (date) => {
  return "JFMAMJJASOND"[date.getMonth()];
};

// --- HELPER: Path Generator (Unchanged) ---
const roundedTopRect = (x, y, w, h, r) => {
  r = Math.min(r, w / 2, h);
  return `
    M ${x},${y + h} L ${x},${y + r} a ${r},${r} 0 0 1 ${r},-${r} 
    L ${x + w - r},${y} a ${r},${r} 0 0 1 ${r},${r} L ${x + w},${
      y + h
    } Z                                    
  `;
};

// --- HELPER: Resolve the visible window into the footer strip's range text ---
const formatRangeLabel = (start, end, viewMode) => {
  if (!start || !end) return "";

  const fmtDay = d3.timeFormat("%d %b %Y");
  const fmtDayShort = d3.timeFormat("%d %b");
  const fmtMonth = d3.timeFormat("%b %Y");

  if (viewMode === "D" || viewMode === "W" || viewMode === "M") {
    const displayEnd = d3.timeDay.offset(end, -1);
    return `${fmtDayShort(start)} – ${fmtDay(displayEnd)}`;
  }
  if (viewMode === "Y") {
    return `${fmtMonth(start)} – ${fmtMonth(d3.timeMonth.offset(end, -1))}`;
  }
  return `${fmtMonth(start)} – ${fmtMonth(end)}`;
};

export default function TimeSeries({
  config,
  data,
  onNodeSelect = () => {},
  onNodeHover = () => {},
  onViewStateChange = () => {},
  viewMode,
  viewOffset,
  setViewOffset,
  currentGraphId = null,
  selectedId = null,
}) {
  const chartRef = useRef(null);
  const touchStart = useRef(null);
  const touchEnd = useRef(null);
  // A horizontal swipe pages the window AND synthesises a click at touchend.
  // Without this flag, paging would also select the day under the finger.
  const swiped = useRef(false);
  const prevViewOffset = useRef(0);
  const minSwipeDistance = 50;

  const { viewType } = useResponsiveState();
  const isMobile = viewType === "mobile"; // Helper boolean

  // The tile is fluid now (it used to be a fixed 800x400), so the d3 render
  // has to re-run whenever its box changes, not just on viewType breakpoints.
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = chartRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setChartSize((prev) =>
        prev.width === width && prev.height === height
          ? prev
          : { width, height },
      );
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const isRate = () => {
    return currentGraphId === "tasks_completion_rate";
  };

  // --- SWIPE LOGIC FOR PAGE NAVIGATION ---
  const onTouchStart = (e) => {
    touchEnd.current = null; // Reset touch end
    swiped.current = false;
    touchStart.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    touchEnd.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStart.current || !touchEnd.current) return;
    if (viewMode === "A") return;

    const distance = touchStart.current - touchEnd.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe || isRightSwipe) {
      swiped.current = true;
    }

    if (isLeftSwipe) {
      if (viewOffset > 0) {
        setViewOffset((prev) => prev - 1);
      }
    }

    if (isRightSwipe) {
      setViewOffset((prev) => prev + 1);
    }
  };

  // --- 1. MEMOIZE DATA & DOMAIN ---
  const { processedData, xDomain } = useMemo(() => {
    const pData = processData(config.graphData, viewMode);

    if (!pData || pData.length === 0)
      return { processedData: [], xDomain: [new Date(), new Date()] };

    const maxDate = d3.max(pData, (d) => new Date(d.category));
    let domain;

    if (viewMode === "D" || viewMode === "W") {
      const anchorDate = d3.timeDay.offset(maxDate, -7 * viewOffset);
      const startDate = d3.timeDay.offset(anchorDate, -6);
      const endDate = d3.timeDay.offset(anchorDate, 1);
      domain = [startDate, endDate];
    } else if (viewMode === "M") {
      const anchorDate = d3.timeDay.offset(maxDate, -30 * viewOffset);
      const startDate = d3.timeDay.offset(anchorDate, -29);
      const endDate = d3.timeDay.offset(anchorDate, 1);
      domain = [startDate, endDate];
    } else if (viewMode === "Y") {
      const anchorDate = d3.timeYear.offset(maxDate, -viewOffset);
      const startYear = d3.timeMonth.offset(anchorDate, -11);
      const nextMonth = d3.timeMonth.offset(anchorDate, 1);
      domain = [startYear, nextMonth];
    } else if (viewMode === "A") {
      const [minD, maxD] = d3.extent(pData, (d) => new Date(d.category));
      const paddedEnd = d3.timeDay.offset(maxD, 1);
      domain = [minD, paddedEnd];
    } else {
      domain = d3.extent(pData, (d) => new Date(d.category));
    }

    return { processedData: pData, xDomain: domain };
  }, [config.graphData, viewMode, viewOffset]);

  // --- 2. CALCULATE VISIBLE DATA ---
  const visibleData = useMemo(() => {
    const [minTime, maxTime] = xDomain;
    return processedData.filter(
      (d) => new Date(d.category) >= minTime && new Date(d.category) < maxTime,
    );
  }, [processedData, xDomain]);

  const currentAverage = useMemo(() => {
    if (visibleData.length === 0) return 0;
    return isRate()
      ? d3.mean(visibleData, (d) => d.value) * 100
      : d3.mean(visibleData, (d) => d.value);
  }, [visibleData]);

  // Report the resolved window and average up to the screen, which feeds both
  // the hoisted ChartHeader and the footer strip's window stepper. React-layer
  // only — the d3 drawing code below is untouched by this.
  const rangeLabel = useMemo(
    () => formatRangeLabel(xDomain[0], xDomain[1], viewMode),
    [xDomain, viewMode],
  );

  useEffect(() => {
    onViewStateChange({
      rangeLabel,
      average: currentAverage,
      visibleCount: visibleData.length,
    });
  }, [rangeLabel, currentAverage, visibleData.length, onViewStateChange]);

  // --- 3. D3 RENDER EFFECT ---
  useEffect(() => {
    if (!chartRef.current) return;

    const chartContainer = d3.select(chartRef.current);
    const { width: containerW, height: containerH } = chartContainer
      .node()
      .getBoundingClientRect();

    const margin =
      viewType === "mobile"
        ? config.margin.mobile
        : config.margin.desktop || { top: 40, right: 60, bottom: 60, left: 20 };

    const width = containerW - margin.left - margin.right;
    const height = containerH - margin.top - margin.bottom;

    // --- A. SETUP SVG LAYERS ---
    let svg = chartContainer.select("svg");
    let plotGroup,
      xAxisGroup,
      yAxisGroup,
      gridGroup,
      yearBoundaryGroup,
      cursorLine,
      interactionRect;

    if (svg.empty()) {
      svg = chartContainer
        .append("svg")
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom);

      const mainGroup = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      // 1. Clip Path
      mainGroup
        .append("defs")
        .append("clipPath")
        .attr("id", "plot-clip")
        .append("rect")
        .attr("width", width)
        .attr("height", height);

      // 2. Static Groups
      gridGroup = mainGroup.append("g").attr("class", "grid-vertical");
      yearBoundaryGroup = mainGroup
        .append("g")
        .attr("class", "year-boundaries");
      plotGroup = mainGroup
        .append("g")
        .attr("class", "plot-area")
        .attr("clip-path", "url(#plot-clip)");
      xAxisGroup = mainGroup.append("g").attr("class", "axis x-axis");
      yAxisGroup = mainGroup.append("g").attr("class", "axis y-axis");

      // 3. Cursor Line (For scrubbing)
      cursorLine = mainGroup
        .append("line")
        .attr("class", "chart-cursor-line")
        .attr("y1", 0)
        .attr("y2", height)
        .attr("opacity", 0); // Hidden by default

      // 4. Interaction Overlay (Invisible Rect)
      interactionRect = mainGroup
        .append("rect")
        .attr("class", "interaction-overlay")
        .attr("width", width)
        .attr("height", height)
        .style("fill", "transparent")
        .style("cursor", "crosshair");
    } else {
      // Re-select
      const mainGroup = svg.select("g");
      gridGroup = mainGroup.select(".grid-vertical");
      yearBoundaryGroup = mainGroup.select(".year-boundaries");
      plotGroup = mainGroup.select(".plot-area");
      xAxisGroup = mainGroup.select(".axis.x-axis");
      yAxisGroup = mainGroup.select(".axis.y-axis");
      cursorLine = mainGroup.select(".chart-cursor-line");
      interactionRect = mainGroup.select(".interaction-overlay");

      // Update dimensions
      svg
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom);
      svg.select("#plot-clip rect").attr("width", width).attr("height", height);
      cursorLine.attr("y2", height);
      interactionRect.attr("width", width).attr("height", height);
    }

    // --- B. SCALES & AXES ---
    const y = d3.scaleLinear().domain(config.yRange).range([height, 0]).nice();
    const x = d3.scaleTime().range([0, width]);
    x.domain(xDomain);

    const [domainStart, domainEnd] = x.domain();
    let tickValues;
    let allTimeSpanDays = 0;
    if (viewMode === "Y") {
      tickValues = d3.timeMonth.range(domainStart, domainEnd);
    } else if (viewMode === "M") {
      tickValues = d3.timeDay.every(3).range(domainStart, domainEnd);
    } else if (viewMode === "A") {
      allTimeSpanDays = d3.timeDay.count(domainStart, domainEnd);
      if (isMobile) {
        if (allTimeSpanDays <= 30) {
          tickValues = d3.timeWeek.range(domainStart, domainEnd);
        } else if (allTimeSpanDays <= 90) {
          tickValues = d3.timeMonth.range(domainStart, domainEnd);
        } else if (allTimeSpanDays <= 365) {
          tickValues = d3.timeMonth.range(domainStart, domainEnd);
        } else if (allTimeSpanDays <= 730) {
          tickValues = d3.timeMonth.every(2).range(domainStart, domainEnd);
        } else {
          tickValues = d3.timeMonth.every(3).range(domainStart, domainEnd);
        }
      } else {
        if (allTimeSpanDays <= 60) {
          tickValues = d3.timeWeek.range(domainStart, domainEnd);
        } else if (allTimeSpanDays <= 365) {
          tickValues = d3.timeMonth.range(domainStart, domainEnd);
        } else if (allTimeSpanDays <= 730) {
          tickValues = d3.timeMonth.every(2).range(domainStart, domainEnd);
        } else {
          tickValues = d3.timeMonth.every(3).range(domainStart, domainEnd);
        }
      }
    } else {
      tickValues = d3.timeDay.range(domainStart, domainEnd);
    }

    let xAxis = d3.axisBottom(x).tickValues(tickValues);
    if (viewMode === "D") xAxis.tickFormat(d3.timeFormat("%d"));
    else if (viewMode === "W") xAxis.tickFormat(d3.timeFormat("%a"));
    else if (viewMode === "M") xAxis.tickFormat(d3.timeFormat("%d"));
    else if (viewMode === "Y") xAxis.tickFormat(d3.timeFormat("%b"));
    else if (viewMode === "A") {
      // No year in the label at any span — the year boundary is drawn as a
      // marked gridline below instead, which reads better than repeating "'25".
      if (isMobile) {
        if (allTimeSpanDays <= 30) {
          xAxis.tickFormat(d3.timeFormat("%b %d"));
        } else if (allTimeSpanDays <= 90) {
          xAxis.tickFormat(d3.timeFormat("%b"));
        } else {
          xAxis.tickFormat(monthLetterFormat);
        }
      } else {
        if (allTimeSpanDays <= 60) {
          xAxis.tickFormat(d3.timeFormat("%b %d"));
        } else {
          xAxis.tickFormat(d3.timeFormat("%b"));
        }
      }
    }

    const yAxis = d3.axisRight(y).ticks().tickSize(-width).tickPadding(10);
    if (config.yRange[1] === 15) {
      yAxis.tickValues([0, 5, 10, 15]).ticks(4);
    } else if (isRate()) {
      yAxis.tickFormat(d3.format(".0%")).ticks(5);
    } else {
      yAxis.ticks(5).tickFormat(d3.format("d"));
    }

    xAxisGroup.attr("transform", `translate(0,${height})`).call(xAxis);

    yAxisGroup
      .attr("transform", `translate(${width}, 0)`)
      .transition()
      .duration(500)
      .call(yAxis);

    yAxisGroup.selectAll(".tick line").attr("stroke-opacity", 0.3);
    yAxisGroup.select(".domain").remove();

    const gridAxis = d3
      .axisBottom(x)
      .tickSize(-height)
      .tickFormat("")
      .tickValues(tickValues);
    gridGroup.attr("transform", `translate(0, ${height})`).call(gridAxis);

    // Year boundaries, drawn only in All-time where the tick labels carry no
    // year. Each line marks the turn from one year into the next.
    const yearStarts =
      viewMode === "A"
        ? d3.timeYear.range(domainStart, domainEnd).filter((t) => x(t) > 0)
        : [];

    yearBoundaryGroup
      .selectAll("g.year-boundary")
      .data(yearStarts, (d) => d.getTime())
      .join(
        (enter) => {
          const g = enter.append("g").attr("class", "year-boundary");
          g.append("line").attr("y1", 0).attr("y2", height);
          g.append("text").attr("y", 12).attr("dx", 5);
          return g;
        },
        (update) => update,
        (exit) => exit.remove(),
      )
      .attr("transform", (d) => `translate(${x(d)}, 0)`)
      .call((g) => g.select("line").attr("y2", height))
      .call((g) => g.select("text").text((d) => d3.timeFormat("%Y")(d)));

    // --- C. INTERACTION LOGIC (Scrubbing / Mobile Lock) ---

    // 1. Bisector to find nearest date
    const bisectDate = d3.bisector((d) => new Date(d.category)).left;

    // 2. Interaction Handlers
    // Resolving the nearest point from the pointer's x makes the whole column
    // a hit target, so a 4px marker never has to be aimed at directly.
    const nearestPoint = (event) => {
      const [mx] = d3.pointer(event, interactionRect.node());
      const invertX = x.invert(mx);

      const i = bisectDate(visibleData, invertX, 1);
      const d0 = visibleData[i - 1];
      const d1 = visibleData[i];

      if (d1 && d0) {
        return invertX - new Date(d0.category) > new Date(d1.category) - invertX
          ? d1
          : d0;
      }
      return d1 || d0 || null;
    };

    const handlePointerMove = (event) => {
      const d = nearestPoint(event);
      if (!d) return;

      // Calculate cursor x position. For bar modes, center on the bar.
      // For All Time (line + markers), use the marker x directly.
      let cursorX;
      if (viewMode === "A") {
        cursorX = x(new Date(d.category));
      } else {
        const nextUnit =
          viewMode === "Y"
            ? d3.timeMonth.offset(domainStart, 1)
            : d3.timeDay.offset(domainStart, 1);
        const oneUnitWidth = x(nextUnit) - x(domainStart);
        const barOffset = oneUnitWidth * 0.1;
        const barWidth = oneUnitWidth * 0.8;
        cursorX = x(new Date(d.category)) + barOffset + barWidth / 2;
      }

      // Leader line down to the hovered point
      cursorLine
        .attr("x1", cursorX)
        .attr("x2", cursorX)
        .attr("opacity", 1)
        .classed("active", true);

      // The highlight is applied imperatively rather than through React state:
      // a re-render here would re-run this whole effect and redraw the plot on
      // every pointer move.
      const matches = (node) =>
        node.category.toString() === d.category.toString();

      plotGroup.selectAll(".data-node").classed("data-node-hovered", false);
      plotGroup
        .selectAll(".data-node")
        .filter(matches)
        .classed("data-node-hovered", true)
        .raise();

      plotGroup.selectAll(".data-marker").classed("active", false);
      plotGroup.selectAll(".data-marker").filter(matches).classed("active", true);

      // The day record on the right is the tooltip now.
      onNodeHover(d);
    };

    const handlePointerLeave = () => {
      cursorLine.attr("opacity", 0).classed("active", false);
      plotGroup.selectAll(".data-node").classed("data-node-hovered", false);
      plotGroup.selectAll(".data-marker").classed("active", false);
      onNodeHover(null);
    };

    // 3. Attach Listeners to Overlay
    interactionRect
      .on("pointermove", handlePointerMove)
      .on("pointerleave", handlePointerLeave)
      .on("click", (event) => {
        // The click that closes a paging swipe is not a selection.
        if (swiped.current) {
          swiped.current = false;
          return;
        }
        const d = nearestPoint(event);
        if (d) onNodeSelect(d);
      });
    // Optional: Prevent default on touchstart if you want to stop scrolling while scrubbing
    // .on("touchstart", (e) => e.preventDefault());

    // --- D. RENDER PLOT AREA ---

    const direction = viewOffset > prevViewOffset.current ? 1 : -1;
    prevViewOffset.current = viewOffset;

    // Scoped to what is on screen, not the whole dataset. Against all-time
    // extremes a windowed view usually flagged nothing at all, because the
    // best and worst days rarely fall inside the seven you are looking at.
    const maxVal = d3.max(visibleData, (d) => d.value);
    const minVal = d3.min(visibleData, (d) => d.value);
    // A single point is not simultaneously the high and the low.
    const hasSpread = visibleData.length > 1 && maxVal !== minVal;
    const isOutlier = (d) =>
      hasSpread && (d.value === maxVal || d.value === minVal);

    // Ties the plot to the day-record panel. Yearly grain aggregates away the
    // per-day id, so nothing matches there — correct, there is no single day.
    const isSelected = (d) => selectedId != null && d.id === selectedId;

    // Slide Out Old (bars or line, both share .plot-container class)
    plotGroup
      .selectAll(".plot-container")
      .transition()
      .duration(500)
      .ease(d3.easeCubicOut)
      .attr("transform", `translate(${direction === 1 ? width : -width}, 0)`)
      .remove();

    const enterX = direction === 1 ? -width : width;

    if (viewMode === "A") {
      // --- LINE + MARKERS RENDER ---
      const newLineGroup = plotGroup
        .append("g")
        .attr("class", "plot-container line-container")
        .attr("transform", `translate(${enterX}, 0)`);

      const lineGenerator = d3
        .line()
        .x((d) => x(new Date(d.category)))
        .y((d) => y(d.value))
        .curve(d3.curveMonotoneX);

      newLineGroup
        .append("path")
        .datum(visibleData)
        .attr("class", "data-line")
        .attr("d", lineGenerator);

      newLineGroup
        .selectAll("circle")
        .data(visibleData)
        .join("circle")
        .attr("class", (d) => {
          const base = isOutlier(d)
            ? "data-node data-marker data-node-outlier"
            : "data-node data-marker";
          return isSelected(d) ? `${base} data-node-selected` : base;
        })
        .attr("cx", (d) => x(new Date(d.category)))
        .attr("cy", (d) => y(d.value))
        .attr("r", 4);

      // Trendline (linear regression over visible data)
      if (visibleData.length >= 2) {
        const xs = visibleData.map((d) => new Date(d.category).getTime());
        const ys = visibleData.map((d) => d.value);
        const n = xs.length;
        const sumX = d3.sum(xs);
        const sumY = d3.sum(ys);
        const sumXY = d3.sum(xs.map((v, i) => v * ys[i]));
        const sumXX = d3.sum(xs.map((v) => v * v));
        const denom = n * sumXX - sumX * sumX;
        if (denom !== 0) {
          const slope = (n * sumXY - sumX * sumY) / denom;
          const intercept = (sumY - slope * sumX) / n;
          const xStart = xs[0];
          const xEnd = xs[xs.length - 1];

          newLineGroup
            .append("line")
            .attr("class", "trendline")
            .attr("x1", x(new Date(xStart)))
            .attr("y1", y(slope * xStart + intercept))
            .attr("x2", x(new Date(xEnd)))
            .attr("y2", y(slope * xEnd + intercept))
            .attr("stroke", "#e2dfcf")
            .attr("stroke-width", 2)
            .attr("stroke-dasharray", "5,5")
            .style("opacity", 0.7);
        }
      }

      newLineGroup
        .transition()
        .duration(500)
        .ease(d3.easeCubicOut)
        .attr("transform", `translate(0, 0)`);
    } else {
      // --- BAR RENDER (D / W / M / Y) ---
      const nextUnit =
        viewMode === "Y"
          ? d3.timeMonth.offset(domainStart, 1)
          : d3.timeDay.offset(domainStart, 1);
      const oneUnitWidth = x(nextUnit) - x(domainStart);
      const barWidth = oneUnitWidth * 0.8;
      const barOffset = oneUnitWidth * 0.1;

      const newBarGroup = plotGroup
        .append("g")
        .attr("class", "plot-container bar-container")
        .attr("transform", `translate(${enterX}, 0)`);

      newBarGroup
        .selectAll("path")
        .data(visibleData)
        .join("path")
        .attr("class", (d) => {
          const base = isOutlier(d)
            ? "data-node data-node-outlier"
            : "data-node data-node-default";
          return isSelected(d) ? `${base} data-node-selected` : base;
        })
        .attr("d", (d) => {
          const xPos = x(new Date(d.category)) + barOffset;
          const yPos = y(d.value);
          return roundedTopRect(xPos, yPos, barWidth, height - y(d.value), 5);
        })
        .on("click", (e, d) => {
          e.stopPropagation();
          onNodeSelect(d);
        });

      newBarGroup
        .transition()
        .duration(500)
        .ease(d3.easeCubicOut)
        .attr("transform", `translate(0, 0)`);
    }
  }, [
    config,
    processedData,
    xDomain,
    visibleData,
    viewMode,
    onNodeSelect,
    onNodeHover,
    viewOffset,
    viewType,
    chartSize,
    selectedId,
  ]);

  const [lowLabel, highLabel] = config.yAxisLabels || [];

  return (
    <div className="chart-area">
      <div className="chart-area__plot">
        {/* y-axis labels as HTML overlays, so the d3 axis code stays untouched */}
        {highLabel && (
          <span className="chart-axis-label chart-axis-label--top">
            {highLabel}
          </span>
        )}
        {lowLabel && (
          <span className="chart-axis-label chart-axis-label--bottom">
            {lowLabel}
          </span>
        )}
        <div
          ref={chartRef}
          className="chart-tile"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        ></div>
      </div>
      {config.xAxisTitle && (
        <div className="chart-axis-title">{config.xAxisTitle}</div>
      )}
    </div>
  );
}
